import { QUOTE_FIELDS } from './common';
import { hhmmss, datetime } from '../../../util/date-time';

import { subtract, multiply, divide } from '../../../util/math';

import { hasProperty, inArray } from '../../../util/jquery';

export function marketsPullQuoteTransform(
    data,
    parma = {},
    cacheData = new Map()
) {
    const array = [];
    data.items = data.items || [];
    data.items.forEach(item => {
        const { fields, specialFields } = getFields(item.fields);
        const instrument = item.ticker;
        const quotes = cacheData.get(instrument);
        const [exch] = item.ticker.split('.');
        if (exch === '21') {
            replaceLastPriceAndClosePrice(fields, specialFields);
        }
        calculateSpread(fields, specialFields, quotes);
        calculateChange(fields, specialFields, quotes);
        calculatePrePostChange('Pre', fields, specialFields, quotes);
        calculatePrePostChange('Post', fields, specialFields, quotes);
        calculateDateTime(fields, specialFields, quotes);
        mergePrePost(fields, specialFields, quotes);
        array.push({
            instrument,
            dataType: item.source,
            fields: fields
        });
    });
    return array;
}

export const CALCULATED_FIELDS = {
    closePrice: 'f',
    bidPrice: 'f',
    askPrice: 'f',
    spread: 'f',
    prePrice: 'f',
    preChg: 'f',
    'preChg%': 'f',
    preDate: 's',
    preTime: 's',
    lastPrice: 'f',
    chg: 'f',
    'chg%': 'f',
    tradeDate: 's',
    tradeTime: 's',
    postPrice: 'f',
    postChg: 'f',
    'postChg%': 'f',
    postDate: 's',
    postTime: 's',
    status: 's',
    previousNAV: 'f',
    NAV: 'f',
    yesterdayClosePrice: 'f'
};

function getFields(fieldArray) {
    const fields = [];
    const specialFields = {};
    fieldArray.forEach(field => {
        if (hasProperty(QUOTE_FIELDS, field.name)) {
            const name = QUOTE_FIELDS[field.name];
            if (/.*Time/i.test(name)) {
                if (/\d+:\d+:\d+/.test(field.value)) {
                    field.value = field.value.split('.')[0];
                } else {
                    field.value = hhmmss(field.value);
                }
            } else if (/.*Date/i.test(name)) {
                field.value = field.value.replace(
                    /(\d+)(-|\.)(\d+)(-|\.)(\d+)/,
                    '$5$3$1'
                );
            }
            if (CALCULATED_FIELDS[name]) {
                specialFields[name] =
                    CALCULATED_FIELDS[name] === 'f'
                        ? parseFloat(field.value)
                        : field.value;
            }
            fields.push({
                name,
                value: field.value
            });
        }
    });
    return {
        fields,
        specialFields
    };
}

function calculateSpread(fields, specialFields, cacheData = new Map()) {
    const spreadKey = QUOTE_FIELDS.Spread;
    const bidKey = QUOTE_FIELDS.BidPrice;
    const askKey = QUOTE_FIELDS.AskPrice;
    if (
        !hasProperty(specialFields, spreadKey) &&
        (hasProperty(specialFields, bidKey) ||
            hasProperty(specialFields, askKey))
    ) {
        const bid = specialFields[bidKey] || +cacheData.get(bidKey);
        const ask = specialFields[askKey] || +cacheData.get(askKey);
        if (bid > 0 && ask > 0) {
            fields.push({
                name: spreadKey,
                value: `${subtract(bid, ask)}`
            });
        }
    }
}

function replaceLastPriceAndClosePrice(fields, specialFields) {
    const preNavKey = QUOTE_FIELDS.PreviousNAV;
    const navKey = QUOTE_FIELDS.NAV;
    const lastPriceKey = QUOTE_FIELDS.LastPrice;
    const closePriceKey = QUOTE_FIELDS.ClosePrice;
    const yesterdayClosePriceKey = QUOTE_FIELDS.D20;
    const replaceField = function(targetKey, newKey) {
        if (hasProperty(specialFields, newKey) && !specialFields[targetKey]) {
            const idx = inArray(fields, item => item.name === targetKey);
            const field = {
                name: targetKey,
                value: `${specialFields[newKey]}`
            };
            specialFields[targetKey] = specialFields[newKey];
            if (idx > -1) {
                fields.splice(idx, 1, field);
            } else {
                fields.push(field);
            }
        }
    };
    replaceField(lastPriceKey, navKey);
    replaceField(closePriceKey, preNavKey);
    replaceField(yesterdayClosePriceKey, preNavKey);
}

function calculateChange(fields, specialFields, cacheData = new Map()) {
    const chgKey = QUOTE_FIELDS.H15;
    const lastPriceKey = QUOTE_FIELDS.LastPrice;
    const closePriceKey = QUOTE_FIELDS.ClosePrice;
    if (
        !hasProperty(specialFields, chgKey) &&
        (hasProperty(specialFields, lastPriceKey) ||
            specialFields[closePriceKey] > 0)
    ) {
        const last = specialFields[lastPriceKey] || +cacheData[lastPriceKey];
        const close = specialFields[closePriceKey] || +cacheData[closePriceKey];
        if (last > 0 && close > 0) {
            const chg = subtract(last, close);
            fields.push({
                name: chgKey,
                value: `${chg}`
            });
            fields.push({
                name: QUOTE_FIELDS.H16,
                value: `${divide(multiply(chg, 100), close)}`
            });
        }
    }
}

function calculatePrePostChange(
    prefix,
    fields,
    specialFields,
    cacheData = new Map()
) {
    const chgKey = QUOTE_FIELDS[`${prefix}Chg`];
    const lastPriceKey = QUOTE_FIELDS[`${prefix}Price`];
    const closePriceKey = QUOTE_FIELDS.LastPrice;

    if (
        !hasProperty(specialFields, chgKey) &&
        (hasProperty(specialFields, lastPriceKey) ||
            hasProperty(specialFields, closePriceKey))
    ) {
        const last = specialFields[lastPriceKey] || +cacheData[lastPriceKey];
        const close = specialFields[closePriceKey] || +cacheData[closePriceKey];
        if (last > 0 && close > 0) {
            const chg = subtract(last, close);
            const chgPercentKey = QUOTE_FIELDS[`${prefix}Chg%`];
            const chgPercent = divide(multiply(chg, 100), close);
            fields.push({
                name: chgKey,
                value: `${chg}`
            });
            fields.push({
                name: chgPercentKey,
                value: `${chgPercent}`
            });
            specialFields[chgKey] = chg;
            specialFields[chgPercentKey] = chgPercent;
        }
    }
}

function calculateDateTime(fields, specialFields, cacheData = new Map()) {
    [
        {
            dateKey: QUOTE_FIELDS.TradeDate,
            timeKey: QUOTE_FIELDS.TradeTime,
            dateTimeKey: 'tradeDateTime'
        },
        {
            dateKey: QUOTE_FIELDS.PostDate,
            timeKey: QUOTE_FIELDS.PostTime,
            dateTimeKey: 'postDateTime'
        },
        {
            dateKey: QUOTE_FIELDS.PreDate,
            timeKey: QUOTE_FIELDS.PreTime,
            dateTimeKey: 'preDateTime'
        }
    ].forEach(d => {
        const { dateKey, timeKey, dateTimeKey } = d;
        if (
            hasProperty(specialFields, dateKey) ||
            hasProperty(specialFields, timeKey)
        ) {
            const date = specialFields[dateKey] || cacheData[dateKey];
            const time = specialFields[timeKey] || cacheData[timeKey];
            if (date && time) {
                const dateTime = `${date} ${time}`;
                fields.push({
                    name: dateTimeKey,
                    value: dateTime
                });
                specialFields[dateTimeKey] = dateTime;
            }
        }
    });
}

function mergePrePost(fields, specialFields, quotes) {
    if (
        (hasProperty(specialFields, 'preDateTime') &&
            hasProperty(specialFields, 'prePrice')) ||
        (hasProperty(specialFields, 'postDateTime') &&
            hasProperty(specialFields, 'postPrice'))
    ) {
        const preDateTime = datetime(
            specialFields[QUOTE_FIELDS.PreDate],
            specialFields[QUOTE_FIELDS.PreTime]
        );
        const postDateTime = datetime(
            specialFields[QUOTE_FIELDS.PostDate],
            specialFields[QUOTE_FIELDS.PostTime]
        );
        let prefix;
        if (postDateTime.getTime() > preDateTime.getTime()) {
            prefix = 'post';
        } else {
            prefix = 'pre';
        }
        [
            {
                name: 'prePostDateTime',
                value: specialFields[`${prefix}DateTime`]
            },
            {
                name: 'prePostDate',
                value: specialFields[`${prefix}Date`]
            },
            {
                name: 'prePostTime',
                value: specialFields[`${prefix}Time`]
            },
            {
                name: 'prePostPrice',
                value: String(specialFields[`${prefix}Price`])
            },
            {
                name: 'prePostChg',
                value: String(specialFields[`${prefix}Chg`])
            },
            {
                name: 'prePostChg%',
                value: String(specialFields[`${prefix}Chg%`])
            }
        ].forEach(d => {
            fields.push({
                name: d.name,
                value: d.value
            });
        });
    }
}
